import { UUID } from '@indigohive/cogfy-types'
import clsx, { ClassValue } from 'clsx'
import { PropsWithChildren, ReactNode } from 'react'
import { avatarInitials, getHashOfString, normalizeHash } from '../../../../helpers'
import { FormattedDate } from '../../../../components'
import { useTranslation } from 'react-i18next'
import { GetChatMessagesPageResultData } from '@indigohive/cogfy-types/endpoints/getChatMessagesPage'
import { ChatMessageBubbleContent } from '../ChatMessageContent'
import { ChatSourcesBubbleContent } from '../ChatSourcesBubbleContent'

export type ChatBubbleColor =
  'neutral' |
  'primary' |
  'secondary' |
  'accent' |
  'info' |
  'success' |
  'warning' |
  'error'

export type ChatBubbleProps = PropsWithChildren<{
  className?: ClassValue
  senderName?: string
  color?: ChatBubbleColor
  avatarImageSrc?: string
  sendDate?: string
  user?: {
    id: UUID | null
    name: string | null
  }
  buttons?: ReactNode
  hideAvatar?: boolean
  onContextMenu?: (event: React.MouseEvent) => void
  fromAuthenticatedUser: boolean
  message?: GetChatMessagesPageResultData
  loading?: boolean
}>

export function ChatBubble (props: ChatBubbleProps) {
  const { className, senderName, color, avatarImageSrc, sendDate, buttons, hideAvatar, fromAuthenticatedUser, message, loading } = props
  const { t } = useTranslation('common')

  return (
    <div
      className={clsx(
        className,
        'chat',
        !fromAuthenticatedUser && 'chat-start',
        fromAuthenticatedUser && 'chat-end'
      )}
    >
      <div
        className={clsx('chat-image avatar', hideAvatar && 'invisible', !avatarImageSrc && 'placeholder')}
        title={senderName || t('Unknown user')}
      >
        <div className={clsx('w-10 rounded-full text-white')}
          style={{ background: avatarImageSrc ? undefined : getDisplayColor(senderName?.replace(' ', '') ?? '') }}
        >
          {avatarImageSrc && <img src={avatarImageSrc} />}
          {!avatarImageSrc && (
            <span className="text-xs w-full h-full flex items-center justify-center">
              {senderName ? avatarInitials(senderName) : '?'}
            </span>
          )}
        </div>
      </div>
      <div
        className={clsx(
          {
            'bg-[#E5F2FB]': color === 'primary',
            'chat-bubble-secondary': color === 'secondary',
            'chat-bubble-accent': color === 'accent',
            'chat-bubble-info': color === 'info',
            'chat-bubble-success': color === 'success',
            'chat-bubble-warning': color === 'warning',
            'chat-bubble-error': color === 'error'
          },
          color === 'neutral' && [
            'bg-base-100',
            'text-base-content'
          ],
          'px-3',
          !loading && 'py-2',
          loading && 'pt-1',
          'rounded-t-lg',
          'cursor-pointer',
          fromAuthenticatedUser ? 'rounded-bl-xl' : 'rounded-br-xl'
        )}
        onContextMenu={event => props.onContextMenu?.(event)}
      >
        <p className='font-semibold text-base'>
          {!fromAuthenticatedUser && (
            senderName
          )}
        </p>
        {loading && (<span className="loading loading-dots loading-xs" />)}
        {!loading && message !== undefined && (
          <>
            <div className="items-end gap-2 max-w-lg">
              <ChatMessageBubbleContent
                className="grow pb-1 w-full break-words"
                content={message.content}
                contentType={message.contentType}
                contentData={message.contentData}
                files={message.files}
                fromAuthenticatedUser={fromAuthenticatedUser}
              />
              <div className='flex justify-between items-center'>
                <ChatSourcesBubbleContent message={message} />
                {sendDate && (
                  <p
                    className={clsx(
                      'text-xs',
                      'text-slate-400',
                      'text-end'
                    )}
                  >
                    <FormattedDate date={sendDate} format="p" />
                  </p>
                )}
              </div>
            </div>
            {buttons && (
              <div className="flex items-end gap-2">
                {buttons}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function getDisplayColor (value: string) {
  const hash = getHashOfString(value)
  const h = normalizeHash(hash, 0, 360)
  const s = normalizeHash(hash, 50, 75)
  const l = normalizeHash(hash, 25, 60)

  return `hsl(${h}, ${s}%, ${l}%)`
}
